/**
 * ClientStaging — Public staging page for sign + pay flow
 * Route: /staging/:token (no auth required)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText, CheckCircle, CreditCard, AlertCircle, Clock,
  ExternalLink, Shield
} from 'lucide-react';
import DOMPurify from 'dompurify';
import SignatureCapture from '../components/shared/SignatureCapture';

const API_BASE = '/api/staging';

const ClientStaging = () => {
  const [firmName, setFirmName] = useState('');
  useEffect(() => {
    fetch('/api/public/firm-info')
      .then(r => r.json())
      .then(d => setFirmName(d.firm_name || ''))
      .catch(() => {});
  }, []);

  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/public/${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to load staging page');
      }
      const result = await res.json();
      setData(result);
      setError(null);

      // Auto-advance step
      if (result.signature_completed && !result.payment_completed) {
        setActiveStep(2);
      } else if (result.signature_completed && result.payment_completed) {
        setActiveStep(3);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll for payment status every 30s
  useEffect(() => {
    if (!data || data.status !== 'pending' || data.payment_completed) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [data, fetchData]);

  // Re-fetch on window focus (handles Square redirect back)
  useEffect(() => {
    const onFocus = () => {
      if (data && data.status === 'pending') {
        fetchData();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [data, fetchData]);

  // Detect Square redirect params in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('transactionId') || params.get('checkoutId')) {
      // Payment redirect — re-fetch to get updated status
      setTimeout(fetchData, 2000);
    }
  }, [fetchData]);

  // Auto-redirect to portal setup after completion
  const [countdown, setCountdown] = useState(5);
  useEffect(() => {
    if (data && data.status === 'completed' && data.portal_setup_url) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            window.location.href = data.portal_setup_url;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [data]);

  const handleSign = async (signatureData, method, pin) => {
    const res = await fetch(`${API_BASE}/public/${token}/sign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        signature_data: signatureData,
        signature_method: method,
        access_pin: pin
      })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to sign document');
    }
    await fetchData();
  };

  const handleDecline = async (reason, pin) => {
    const res = await fetch(`${API_BASE}/public/${token}/decline`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason, access_pin: pin })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to decline');
    }
    await fetchData();
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-virgo-navy-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400 mx-auto mb-4"></div>
          <p className="text-virgo-teal-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-virgo-navy-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-virgo-navy-800 rounded-2xl shadow-xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Unable to Load</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            If you believe this is an error, please contact our office.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const isCompleted = data.status === 'completed';
  const isCancelled = data.status === 'cancelled';
  const isExpired = data.status === 'expired';

  // Completed state — auto-redirect to portal setup
  if (isCompleted) {
    return (
      <div className="min-h-screen bg-virgo-navy-900">
        <StagingHeader firmName={firmName} />
        <div className="max-w-2xl mx-auto p-4 mt-8">
          <div className="bg-virgo-navy-800 rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Welcome to {firmName || "Our Firm"}</h2>
            <p className="text-virgo-teal-300 mb-6">
              Your engagement is confirmed and your case has been created.
            </p>
            {data.case_display_number && (
              <div className="bg-virgo-navy-700 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-400">Your Case Number</p>
                <p className="text-2xl font-bold text-virgo-teal-400">{data.case_display_number}</p>
              </div>
            )}
            {data.portal_setup_url ? (
              <>
                <p className="text-virgo-teal-300 mb-4">
                  Redirecting to portal setup in <span className="text-white font-bold text-lg">{countdown}</span> seconds...
                </p>
                <a
                  href={data.portal_setup_url}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-semibold hover:from-cyan-600 hover:to-blue-600 transition-all"
                >
                  <Shield className="w-5 h-5" />
                  Continue Now
                </a>
              </>
            ) : (
              <p className="text-sm text-gray-400">
                Check your email for portal setup instructions.
              </p>
            )}
            <p className="text-sm text-gray-500 mt-6">
              Your attorney will be in touch with you shortly. If you have any questions,
              please contact our office at (904) 664-2578.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Cancelled/expired state
  if (isCancelled || isExpired) {
    return (
      <div className="min-h-screen bg-virgo-navy-900">
        <StagingHeader firmName={firmName} />
        <div className="max-w-2xl mx-auto p-4 mt-8">
          <div className="bg-virgo-navy-800 rounded-2xl shadow-xl p-8 text-center">
            <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              {isCancelled ? 'Engagement Declined' : 'Link Expired'}
            </h2>
            <p className="text-gray-400 mb-6">
              {isCancelled
                ? 'This engagement has been declined or cancelled.'
                : 'This link has expired. Please contact our office for a new invitation.'}
            </p>
            <p className="text-sm text-gray-500">
              Contact us: 
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Active staging flow
  return (
    <div className="min-h-screen bg-virgo-navy-900">
      <StagingHeader firmName={firmName} />

      <div className="max-w-3xl mx-auto p-4">
        {/* Client greeting */}
        <div className="text-center mb-6 mt-4">
          <h1 className="text-2xl font-bold text-white">
            Welcome, {data.client_name || 'Valued Client'}
          </h1>
          <p className="text-virgo-teal-300 mt-1">
            Please complete the steps below to begin your{' '}
            {data.practice_area ? data.practice_area.toLowerCase() : 'legal'} representation.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <StepIndicator
            step={1}
            label="Sign Engagement Letter"
            completed={data.signature_completed}
            active={activeStep === 1}
            onClick={() => !data.signature_completed && setActiveStep(1)}
          />
          <div className="w-12 h-0.5 bg-virgo-navy-600"></div>
          <StepIndicator
            step={2}
            label="Pay Retainer"
            completed={data.payment_completed}
            active={activeStep === 2}
            onClick={() => data.signature_completed && !data.payment_completed && setActiveStep(2)}
          />
        </div>

        {/* Step 1: Engagement Letter + Signature */}
        {activeStep === 1 && !data.signature_completed && (
          <div>
            {/* Engagement Letter */}
            <div className="bg-virgo-navy-800 rounded-2xl shadow-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-virgo-teal-400" />
                <h2 className="text-lg font-semibold text-white">Engagement Letter</h2>
              </div>
              {data.engagement_letter_html ? (
                <div
                  className="bg-white text-gray-900 rounded-lg p-6 max-h-[500px] overflow-y-auto text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(data.engagement_letter_html)
                  }}
                />
              ) : (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">Document content loading...</p>
                </div>
              )}
            </div>

            {/* Signature Capture */}
            <SignatureCapture
              signerName={data.signer_name || data.client_name || ''}
              onSign={handleSign}
              onDecline={handleDecline}
              showDecline={true}
              disabled={false}
            />
          </div>
        )}

        {/* Step 1 completed badge */}
        {data.signature_completed && (
          <div className="bg-green-900/30 border border-green-700/50 rounded-xl p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
            <div>
              <p className="text-green-300 font-semibold">Engagement Letter Signed</p>
              <p className="text-green-400/60 text-sm">
                Signed on {data.signature_completed_at
                  ? new Date(data.signature_completed_at).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric',
                      hour: 'numeric', minute: '2-digit'
                    })
                  : 'just now'}
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Payment */}
        {(activeStep === 2 || (data.signature_completed && !data.payment_completed)) && (
          <div className="bg-virgo-navy-800 rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-6 h-6 text-virgo-teal-400" />
              <h2 className="text-lg font-semibold text-white">Retainer Payment</h2>
            </div>

            <div className="bg-virgo-navy-700 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                {data.invoice_number && (
                  <div>
                    <p className="text-sm text-gray-400">Invoice</p>
                    <p className="text-white font-medium">{data.invoice_number}</p>
                  </div>
                )}
                {data.retainer_amount && (
                  <div>
                    <p className="text-sm text-gray-400">Amount Due</p>
                    <p className="text-2xl font-bold text-virgo-teal-400">
                      ${Number(data.retainer_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {data.invoice_due_date && (
                  <div>
                    <p className="text-sm text-gray-400">Due Date</p>
                    <p className="text-white font-medium">
                      {new Date(data.invoice_due_date).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'long', day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-400">Practice Area</p>
                  <p className="text-white font-medium">{data.practice_area || 'Legal Services'}</p>
                </div>
              </div>
            </div>

            {data.square_payment_url ? (
              <a
                href={data.square_payment_url}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-semibold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all"
              >
                <CreditCard className="w-5 h-5" />
                Pay via Square
                <ExternalLink className="w-4 h-4" />
              </a>
            ) : (
              <div className="text-center p-4">
                <p className="text-gray-400">Payment link is being generated. Please refresh in a moment.</p>
              </div>
            )}

            <p className="text-center text-sm text-gray-500 mt-4">
              You will be redirected back to this page after completing your payment.
            </p>
          </div>
        )}

        {/* Step 2 completed badge */}
        {data.payment_completed && (
          <div className="bg-green-900/30 border border-green-700/50 rounded-xl p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
            <div>
              <p className="text-green-300 font-semibold">Payment Received</p>
              <p className="text-green-400/60 text-sm">
                Paid on {data.payment_completed_at
                  ? new Date(data.payment_completed_at).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric',
                      hour: 'numeric', minute: '2-digit'
                    })
                  : 'just now'}
              </p>
            </div>
          </div>
        )}

        {/* Contact footer */}
        <div className="text-center mt-8 mb-12 text-sm text-gray-500">
          <p>Questions? Contact us at </p>
          <p className="mt-1">{firmName || "Our Firm"} | </p>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// Sub-components
// ============================================================================

const StagingHeader = ({ firmName }) => (
  <div className="bg-virgo-navy-800 border-b border-virgo-navy-700">
    <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
      <img
        src="/assets/images/virgo_logo.jpg"
        alt={firmName || "Our Firm"}
        className="w-10 h-10 rounded-lg"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
      <div>
        <h1 className="text-lg font-bold text-white">{firmName || "Our Firm"}</h1>
        <p className="text-xs text-gray-400">Client Engagement</p>
      </div>
    </div>
  </div>
);

const StepIndicator = ({ step, label, completed, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
      completed
        ? 'bg-green-900/30 border border-green-700/50 text-green-300'
        : active
          ? 'bg-virgo-teal-600/20 border border-virgo-teal-500/50 text-virgo-teal-300'
          : 'bg-virgo-navy-800 border border-virgo-navy-600 text-gray-500'
    }`}
  >
    {completed ? (
      <CheckCircle className="w-5 h-5 text-green-400" />
    ) : (
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
        active ? 'bg-virgo-teal-600 text-white' : 'bg-virgo-navy-600 text-gray-400'
      }`}>
        {step}
      </span>
    )}
    <span className="text-sm font-medium">{label}</span>
  </button>
);


export default ClientStaging;
